import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_BASE } from './config';
import { connectSocket } from '../socket/socket';
import '../css/LineAuth.css';

const ERROR_MESSAGES = {
  LINE_NOT_CONFIGURED: 'Server ยังไม่ได้ตั้งค่า LINE Login',
  LINE_ACCESS_DENIED: 'ยกเลิกการเข้าสู่ระบบด้วย LINE แล้ว',
  INVALID_LINE_STATE: 'คำขอ LINE หมดอายุหรือไม่ถูกต้อง กรุณาเริ่มใหม่',
  MISSING_LINE_CODE: 'LINE ไม่ได้ส่งรหัสยืนยันกลับมา',
  LINE_AUTH_FAILED: 'ยืนยันบัญชีกับ LINE ไม่สำเร็จ กรุณาลองใหม่'
};

function saveSession(data, navigate) {
  localStorage.setItem('token', data.token);
  localStorage.setItem('user', JSON.stringify(data.user));
  connectSocket(data.token);
  const requestedPath = String(data.returnPath || '');
  const safeReturnPath = requestedPath.startsWith('/') && !requestedPath.startsWith('//')
    ? requestedPath
    : '';
  navigate(safeReturnPath || (data.user.role === 'admin' ? '/dashboard' : '/profile'), {
    replace: true
  });
}

function LineAuth() {
  const location = useLocation();
  const navigate = useNavigate();
  const startedRef = useRef(false);
  const [ticket, setTicket] = useState('');
  const [lineProfile, setLineProfile] = useState(null);
  const [form, setForm] = useState({ playerId: '', password: '' });
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('กำลังตรวจสอบบัญชี LINE…');
  const [showLinkForm, setShowLinkForm] = useState(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const params = new URLSearchParams(location.search);
    const errorCode = params.get('error');
    const nextTicket = params.get('ticket');

    if (errorCode) {
      sessionStorage.setItem('line_redirect_error', ERROR_MESSAGES[errorCode] || 'เข้าสู่ระบบด้วย LINE ไม่สำเร็จ');
      navigate('/register?lineFailed=1', { replace: true });
      return;
    }
    if (!nextTicket) {
      sessionStorage.setItem('line_redirect_error', 'ไม่พบข้อมูลยืนยันจาก LINE');
      navigate('/register?lineFailed=1', { replace: true });
      return;
    }

    setTicket(nextTicket);
    fetch(`${API_BASE}/api/users/line/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ticket: nextTicket })
    })
      .then(async response => {
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.message || 'ตรวจสอบ LINE ไม่สำเร็จ');
        return data;
      })
      .then(data => {
        if (data.needsLink) {
          setLineProfile(data.lineProfile);
          setStatus('link');
          setMessage('ผูก LINE กับ Player ID ของคุณเพียงครั้งเดียว');
          return;
        }
        saveSession(data, navigate);
      })
      .catch(error => {
        sessionStorage.setItem('line_redirect_error', error.message || 'ตรวจสอบ LINE ไม่สำเร็จ');
        navigate('/register?lineFailed=1', { replace: true });
      });
  }, [location.search, navigate]);

  const linkPlayerId = async event => {
    event.preventDefault();
    if (!form.playerId || !form.password) {
      setMessage('กรุณากรอก Player ID และรหัสผ่าน');
      return;
    }

    try {
      setStatus('linking');
      setMessage('กำลังผูกบัญชีอย่างปลอดภัย…');
      const response = await fetch(`${API_BASE}/api/users/line/link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ticket, ...form })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || 'ผูกบัญชีไม่สำเร็จ');
      }
      saveSession(data, navigate);
    } catch (error) {
      setStatus('link');
      setMessage(error.message || 'ผูกบัญชีไม่สำเร็จ');
    }
  };

  const registerAndLinkLine = () => {
    if (!ticket) return;

    sessionStorage.setItem('pending_line_registration', JSON.stringify({
      ticket,
      name: lineProfile?.name || '',
      picture: lineProfile?.picture || ''
    }));
    navigate('/register?lineSignup=1');
  };

  return (
    <main className="line-auth-page">
      <section className="line-auth-card">
        <div className="line-auth-logo">LINE</div>

        {lineProfile?.picture && (
          <img className="line-auth-avatar" src={lineProfile.picture} alt="รูปโปรไฟล์ LINE" />
        )}

        <h1>
          {status === 'link' || status === 'linking'
            ? `สวัสดี ${lineProfile?.name || ''}`
            : 'LINE Login'}
        </h1>
        <p className={status === 'error' ? 'line-auth-error' : ''}>{message}</p>

        {(status === 'link' || status === 'linking') && (
          <>
          <div className="line-auth-new-player">
            <span>เริ่มต้นใช้งาน</span>
            <button
              type="button"
              disabled={status === 'linking'}
              onClick={registerAndLinkLine}
            >
              ✨ สมัครสมาชิกใหม่
            </button>
            <small>สมัครสำเร็จแล้วระบบจะผูก LINE และเข้าสู่ระบบให้อัตโนมัติ</small>
          </div>

          <section className={`line-auth-existing-player ${showLinkForm ? 'is-open' : ''}`}>
            <button
              type="button"
              className="line-auth-link-toggle"
              disabled={status === 'linking'}
              aria-expanded={showLinkForm || status === 'linking'}
              onClick={() => setShowLinkForm(current => !current)}
            >
              <span>🔗 มี Player ID แล้ว — ผูกไอดี LINE</span>
              <b aria-hidden="true">{showLinkForm || status === 'linking' ? '−' : '+'}</b>
            </button>

            {(showLinkForm || status === 'linking') && (
              <form onSubmit={linkPlayerId}>
                <label>
                  <span>Player ID</span>
                  <input
                    inputMode="numeric"
                    pattern="[0-9]*"
                    placeholder="เช่น 123"
                    value={form.playerId}
                    onChange={event => setForm(current => ({
                      ...current,
                      playerId: event.target.value.replace(/\D/g, '')
                    }))}
                  />
                </label>
                <label>
                  <span>รหัสผ่านผู้เล่น</span>
                  <input
                    type="password"
                    placeholder="ยืนยันว่าเป็นเจ้าของ Player ID"
                    value={form.password}
                    onChange={event => setForm(current => ({
                      ...current,
                      password: event.target.value
                    }))}
                  />
                </label>
                <button type="submit" disabled={status === 'linking'}>
                  {status === 'linking' ? 'กำลังผูกบัญชี…' : 'ผูก LINE และเข้าสู่ระบบ'}
                </button>
                <small>ระบบขอรหัสผ่านเพื่อป้องกันผู้อื่นนำ Player ID ของคุณไปผูกบัญชี</small>
              </form>
            )}
          </section>
          </>
        )}

        {status === 'loading' && <div className="line-auth-loader" aria-hidden="true" />}
        {status === 'error' && (
          <div className="line-auth-fallback-actions">
            <button type="button" className="line-auth-register-fallback" onClick={() => navigate('/register?lineFailed=1', { replace: true })}>✨ สมัครสมาชิกแบบปกติ</button>
            <button type="button" className="line-auth-back" onClick={() => navigate('/', { replace: true })}>กลับหน้า Login</button>
            <small>หาก LINE redirect ไม่สำเร็จ สามารถสมัครด้วยชื่อและ Password ได้ทันที</small>
          </div>
        )}
      </section>
    </main>
  );
}

export default LineAuth;
